import * as THREE from 'three';
import { mat, acquire, aimPointOf, clearLine, fireRound, tracers, clamp, registerStreak, unregisterStreak } from './common.js';

/**
 * Watchdog Helo — a support gunship that flies itself.
 *
 * It orbits the area the player was standing in when it was called, pings every
 * contact it can see, and engages them with a chin gun. Unlike the sentry it has
 * height, so it holds angles no ground asset can, and unlike the gunship it does
 * not take the player out of the fight — it fights alongside them.
 *
 * Everything about the escalation is in the orbit: the sentry sits still and
 * covers one lane, the helo moves and covers all of them.
 */

export const TUNING = {
  duration: 42,
  altitude: 21,
  orbitRadius: 27,
  orbitSpeed: 0.42, // rad/s
  arriveTime: 4.5, // s to fly in from off-map

  range: 58,
  damage: 17,
  burst: 6,
  burstGap: 0.075,
  burstCooldown: 0.85,
  acquireInterval: 0.3,
  /** How fast the turret *mesh* swings round. Cosmetic only — see `_shoot`. */
  aimLerp: 7.0,
  /** How closely the mesh must be lined up before the gun is allowed to fire. */
  onTargetDot: 0.995,
  /** Dispersion, radians. The only thing that makes the helo miss. */
  spread: 0.011,

  rotorSpeed: 26, // rad/s
  bank: 0.2, // rad of roll into the orbit
  scale: 1.0,
};

const _target = new THREE.Vector3();
const _muzzle = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _want = new THREE.Vector3();

export class WatchdogHelo {
  static id = 'helo';

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.root = null;
    this.aim = new THREE.Vector3(0, 0, -1);
    this._scratch = [];
    this.centre = new THREE.Vector3();
    registerStreak(ctx, this, 'WATCHDOG');
  }

  _build() {
    if (this.root) return;
    const g = new THREE.Group();
    const hull = mat('hull');
    const dark = mat('hullDark');

    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.9, 2.1, 6, 12), hull);
    body.rotation.z = Math.PI / 2;
    g.add(body);

    const nose = new THREE.Mesh(new THREE.SphereGeometry(0.85, 12, 10), mat('glass'));
    nose.position.x = 1.5;
    nose.scale.set(1.25, 0.85, 0.9);
    g.add(nose);

    const tail = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.34, 3.4, 8), hull);
    tail.rotation.z = Math.PI / 2;
    tail.position.x = -2.5;
    g.add(tail);

    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.7, 1.0, 0.09), hull);
    fin.position.set(-4.0, 0.45, 0);
    g.add(fin);

    // Skids.
    for (const s of [-1, 1]) {
      const skid = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 2.6, 5), dark);
      skid.rotation.z = Math.PI / 2;
      skid.position.set(0.1, -1.05, s * 0.62);
      g.add(skid);
      const strut = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 0.6, 4), dark);
      strut.position.set(0.1, -0.75, s * 0.62);
      g.add(strut);
    }

    // Main rotor — a hub plus four blades, spun as one node.
    const rotor = new THREE.Group();
    rotor.position.set(0.1, 1.05, 0);
    const hub = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 0.28, 8), dark);
    rotor.add(hub);
    for (let i = 0; i < 4; i++) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(6.4, 0.05, 0.34), dark);
      blade.rotation.y = (i / 4) * Math.PI * 2;
      rotor.add(blade);
    }
    g.add(rotor);
    this.rotor = rotor;

    const tailRotor = new THREE.Group();
    tailRotor.position.set(-4.05, 0.45, 0.14);
    for (let i = 0; i < 3; i++) {
      const blade = new THREE.Mesh(new THREE.BoxGeometry(0.06, 1.5, 0.16), dark);
      blade.rotation.z = (i / 3) * Math.PI * 2;
      tailRotor.add(blade);
    }
    g.add(tailRotor);
    this.tailRotor = tailRotor;

    // Chin turret: the thing that actually aims.
    const turret = new THREE.Group();
    turret.position.set(1.3, -0.75, 0);
    const ball = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), dark);
    turret.add(ball);
    const barrel = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.07, 0.9, 6), dark);
    barrel.rotation.x = Math.PI / 2;
    barrel.position.z = 0.45;
    turret.add(barrel);
    const flash = new THREE.Mesh(new THREE.SphereGeometry(0.16, 6, 5), mat('accent'));
    flash.position.z = 0.95;
    flash.visible = false;
    turret.add(flash);
    g.add(turret);
    this.turret = turret;
    this.flash = flash;

    for (const [s, c] of [[-1, 'hostile'], [1, 'accent']]) {
      const nav = new THREE.Mesh(new THREE.SphereGeometry(0.1, 6, 5), mat(c));
      nav.position.set(-0.4, -0.2, s * 0.95);
      g.add(nav);
    }

    this.light = new THREE.PointLight(0xffb45a, 0, 16, 2);
    this.light.position.set(1.3, -0.75, 0);
    g.add(this.light);

    g.scale.setScalar(TUNING.scale);
    g.name = 'streak-helo';
    g.visible = false;
    this.ctx.scene.add(g);
    this.root = g;
  }

  deploy() {
    this._build();
    const p = this.ctx.player;
    this.centre.set(p?.position?.x ?? 0, 0, p?.position?.z ?? 0);
    this.t = 0;
    this.phase = Math.random() * Math.PI * 2;
    this.target = null;
    this.acquireTimer = 0;
    this.burstLeft = 0;
    this.shotTimer = 0;
    this.cooldown = TUNING.arriveTime;
    this.flashTimer = 0;
    this.root.visible = true;
    this._place(0);

    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.8, pitch: 0.55 });
    return true;
  }

  _place(dt) {
    this.phase += dt * TUNING.orbitSpeed;
    const arrive = clamp(this.t / TUNING.arriveTime, 0, 1);
    // Ease in from twice the orbit radius and half again the altitude.
    const r = TUNING.orbitRadius * (2.2 - 1.2 * arrive);
    const y = TUNING.altitude * (1.7 - 0.7 * arrive);
    const x = this.centre.x + Math.cos(this.phase) * r;
    const z = this.centre.z + Math.sin(this.phase) * r;
    this.root.position.set(x, y, z);
    // Nose along the tangent, banked into the turn. Same derivation as the UAV:
    // the airframe is nose-along-+X, so the tangent yaw is -φ - π/2 and the roll
    // axis is the model's own +X.
    this.root.rotation.set(0, -this.phase - Math.PI / 2, 0);
    this.root.rotateX(TUNING.bank);
  }

  update(dt) {
    this.t += dt;
    if (this.t >= TUNING.duration) {
      this.end();
      return false;
    }
    this._place(dt);

    this.rotor.rotation.y += dt * TUNING.rotorSpeed;
    this.tailRotor.rotation.x += dt * TUNING.rotorSpeed * 1.6;

    this.root.updateMatrixWorld(true);
    this.turret.getWorldPosition(_muzzle);

    /* ---- acquisition ---- */
    this.acquireTimer -= dt;
    if (this.target && (this.target.dead || !clearLine(this.ctx, _muzzle, aimPointOf(this.target, _target)))) {
      this.target = null;
    }
    if (!this.target && this.acquireTimer <= 0) {
      this.acquireTimer = TUNING.acquireInterval;
      this.target = acquire(this.ctx, _muzzle, TUNING.range, null, -1, this._scratch, this);
      // Spotting is itself useful: the helo paints what it sees.
      if (this.target) this.sys.revealActor(this.target, 3.5);
    }

    /* ---- turret slew ---- */
    let onTarget = false;
    if (this.target) {
      aimPointOf(this.target, _target);
      _want.subVectors(_target, _muzzle).normalize();
      this.aim.lerp(_want, Math.min(1, TUNING.aimLerp * dt)).normalize();
      onTarget = this.aim.dot(_want) > TUNING.onTargetDot;
      // Point the turret group down the aim vector, in world space.
      //
      // `Object3D.lookAt` is not `Matrix4.lookAt`: for a plain object it points
      // local **+Z** at the target (only cameras and lights use -Z). The barrel is
      // built along +Z, so this is already correct and must not be flipped.
      _target.copy(_muzzle).add(this.aim);
      this.turret.lookAt(_target);
    }

    /* ---- fire control ---- */
    this.cooldown -= dt;
    this.shotTimer -= dt;
    if (this.target && onTarget) {
      if (this.burstLeft > 0) {
        if (this.shotTimer <= 0) {
          this._shoot(_muzzle);
          this.shotTimer = TUNING.burstGap;
          if (--this.burstLeft === 0) this.cooldown = TUNING.burstCooldown;
        }
      } else if (this.cooldown <= 0) {
        this.burstLeft = TUNING.burst;
        this.shotTimer = 0;
      }
    }

    if (this.flashTimer > 0) {
      this.flashTimer -= dt;
      if (this.flashTimer <= 0) {
        this.flash.visible = false;
        this.light.intensity = 0;
      } else this.light.intensity = this.flashTimer * 150;
    }
    return true;
  }

  /**
   * One round.
   *
   * Fires along the **true** direction to the target, not along `this.aim`.
   *
   * `this.aim` is a smoothed vector, and the helo is moving at roughly 11 m/s
   * around its orbit, so the bearing to a target 25 m away swings at ~0.44 rad/s.
   * A lerp at rate 7 lags that by ~0.06 rad in the steady state — about 3.6
   * degrees, which is 1.6 m of miss at 25 m and over 3 m at 50. Measured: ninety
   * rounds fired, not one point of damage dealt. The smoothed vector is for the
   * turret model and for deciding when the gun is pointed the right way; the ray
   * has to come off the real bearing, with dispersion as the only source of miss.
   */
  _shoot(muzzle) {
    if (!this.target) return;
    aimPointOf(this.target, _target);
    _dir.subVectors(_target, muzzle).normalize();
    const s = TUNING.spread;
    _dir.x += (Math.random() - 0.5) * s;
    _dir.y += (Math.random() - 0.5) * s;
    _dir.z += (Math.random() - 0.5) * s;
    _dir.normalize();

    const start = _want.copy(muzzle).addScaledVector(_dir, 1.1);
    const hit = fireRound(this.ctx, start, _dir, TUNING.range + 20, TUNING.damage, { source: this, weapon: 'helo' });
    const end = hit?.point ?? start.clone().addScaledVector(_dir, TUNING.range);
    tracers(this.ctx).fire(start, end);

    this.flash.visible = true;
    this.flashTimer = 0.05;
    this.light.intensity = 7;
    this.ctx.events.emit('audio:play', {
      id: 'enemy_fire',
      position: { x: muzzle.x, y: muzzle.y, z: muzzle.z },
      volume: 0.75,
      pitch: 1.12,
    });
  }

  end() {
    if (!this.root) return;
    this.root.visible = false;
    this.light.intensity = 0;
    this.flash.visible = false;
  }

  status() {
    return `${Math.max(0, Math.ceil(TUNING.duration - this.t))}s`;
  }

  dispose() {
    unregisterStreak(this.ctx, this);
    if (!this.root) return;
    this.root.parent?.remove(this.root);
    this.root.traverse((o) => o.geometry?.dispose?.());
    this.root = null;
  }
}
